import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useRecoveryDependencyProgress } from '../hooks'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import type { RecoveryDependencyProgressRow } from '../api'
import { UserNameWithPresence } from '@/components/ui/UserNameWithPresence'
import { formatDateForLocale } from '@/lib/dateUtils'
import { getStatusPillClass } from './RecoveryTrackingHeader'
import { buildIconPath } from '@/lib/imagePaths'
import { EntityIcon } from '@/components/ui/entity-icon'
import { isGo, hasCaveats, waitGoStyle, waitGoTitle, type WaitGoCounts } from './WaitGoState'

/**
 * Square display for Wait/Go. Fill carries go/wait; an amber border says a
 * bypassable dependency is still outstanding, so this can proceed only with
 * the bypass applied. See WaitGoState for the four states.
 */
function WaitGoSquare({ value, row, t }: { value: string; row?: WaitGoCounts; t: (key: string) => string }) {
    const counts: WaitGoCounts = row ?? { waitGo: value }
    const go = isGo(counts)
    return (
        <span
            className="inline-flex w-12 h-12 items-center justify-center rounded text-sm font-medium"
            style={waitGoStyle(counts)}
            title={waitGoTitle(counts)}
        >
            {go ? t('recoverRequests.assign') : 'WAIT'}
            {hasCaveats(counts) && <span className="ml-0.5" aria-hidden>*</span>}
        </span>
    )
}

/** Small doughnut chart for percentage complete (0-100) in table cell */
function DoughnutChart({ percentage, size = 32 }: { percentage: number; size?: number }) {
    const r = (size - 4) / 2
    const circumference = 2 * Math.PI * r
    const pct = Math.min(100, Math.max(0, percentage))
    const strokeDashoffset = circumference * (1 - pct / 100)
    return (
        <div className="relative flex-shrink-0 inline-flex" style={{ width: size, height: size }} title={`${Math.round(pct)}% complete`}>
            <svg width={size} height={size} className="rotate-[-90deg]">
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={3}
                    className="text-muted-foreground/30"
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={3}
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    className="text-green-600 transition-all duration-300"
                />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-medium text-foreground">
                {Math.round(pct)}%
            </span>
        </div>
    )
}

function normalizeUtcDate(datetime: string | Date | null | undefined): string | null {
    if (!datetime) return null
    if (datetime instanceof Date) return datetime.toISOString()
    if (typeof datetime === 'string') {
        let dateStr = datetime.includes('T') ? datetime : datetime.replace(' ', 'T')
        if (!dateStr.includes('Z') && !dateStr.match(/[+-]\d{2}:\d{2}$/)) {
            dateStr = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z'
        }
        return dateStr
    }
    return null
}

function formatDate(datetime: string | Date | null | undefined, locale: string) {
    const normalized = normalizeUtcDate(datetime)
    if (!normalized) return '-'
    try {
        return formatDateForLocale(normalized, locale, {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        })
    } catch {
        return '-'
    }
}

interface SubObjectProgressDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    mainId: string | null
    objectDescription?: string | null
}

export function SubObjectProgressDialog({
    open,
    onOpenChange,
    mainId,
    objectDescription,
}: SubObjectProgressDialogProps) {
    const { t, i18n } = useTranslation()
    const { data: rows = [], isLoading } = useRecoveryDependencyProgress(mainId, open)

    const headerTitle = mainId ?? t('recoverRequests.subObjectProgress', 'Sub-object progress')

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-6xl w-[90vw] max-h-[85vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle asChild>
                        <div>
                            <span className="block">{headerTitle}</span>
                            {objectDescription && (
                                <span className="block text-sm font-normal text-muted-foreground mt-0.5">
                                    {objectDescription}
                                </span>
                            )}
                        </div>
                    </DialogTitle>
                </DialogHeader>
                <div className="overflow-auto flex-1 -mx-6 px-6">
                    {isLoading ? (
                        <p className="text-sm text-muted-foreground py-4">
                            {t('common.loading', 'Loading...')}
                        </p>
                    ) : rows.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4">
                            {t('recoverRequests.noSubObjectProgress', 'No sub-object progress data.')}
                        </p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('recoverRequests.objectID', 'Object ID')}</TableHead>
                                    <TableHead>{t('recoverRequests.engineerName', 'Engineer Name')}</TableHead>
                                    <TableHead className="w-24">{t('recoverRequests.assignGo', 'Assign Go')}</TableHead>
                                    <TableHead className="text-right">{t('recoverRequests.percentCompleteColumn', '% Complete')}</TableHead>
                                    <TableHead className="min-w-[17rem]">{t('recoverRequests.forecastStartTime', 'Forecast start')}</TableHead>
                                    <TableHead className="min-w-[17rem]">{t('recoverRequests.forecastFinishTime', 'Forecast finish')}</TableHead>
                                    <TableHead>{t('recoverRequests.assignmentStatus', 'Status')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {(rows as RecoveryDependencyProgressRow[]).map((row, idx) => (
                                    <TableRow key={`${row.SourceID}-${idx}`}>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium">{row.SourceID ?? '-'}</span>
                                                {row.IconFilename && (
                                                    <EntityIcon src={buildIconPath(row.IconFilename)} alt="" className="h-7 w-7 shrink-0" />
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>{row.EngineerName ? (row.Engineer != null && !isNaN(Number(row.Engineer)) ? <UserNameWithPresence userId={Number(row.Engineer)} name={row.EngineerName} /> : row.EngineerName) : '-'}</TableCell>
                                        <TableCell className="w-24">
                                            {row.WaitGo ? (
                                                <WaitGoSquare value={row.WaitGo} row={row as unknown as WaitGoCounts} t={t} />
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {row.PercentComplete != null ? (
                                                <div className="flex justify-end">
                                                    <DoughnutChart percentage={row.PercentComplete} size={36} />
                                                </div>
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap min-w-[17rem]">
                                            {formatDate(row.ForecastStartTime, i18n.language)}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap min-w-[17rem]">
                                            {formatDate(row.ForecastFinishTime, i18n.language)}
                                        </TableCell>
                                        <TableCell>
                                            {row.AssignmentStatus ? (
                                                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getStatusPillClass(row.AssignmentStatus)}`}>
                                                    {row.AssignmentStatus}
                                                </span>
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}
